import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import clsx from "clsx";

import { post, patch } from "@/lib/api-client";
import { agentApi } from "@/lib/extensions-api";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import type { Category, Product } from "@/types/catalog";
import type { Agent } from "@/types/agent";

interface Form {
  product_code: string;
  name: string;
  brand: string;
  category_id: string;
  vendor_id: string;
  short_description: string;
  description: string;
  base_price: string;
  compare_at_price: string;
  qualification_point_value: number;
  status: "active" | "inactive";
  tags: string; // Comma separated string
}

interface Props {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  categories: Category[];
  onSaved: () => void;
}

export function ProductFormModal({ open, onClose, product, categories, onSaved }: Props) {
  const isEdit = !!product;
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);

  // Fetch agents so Admin can assign this product to a Vendor
  const agentsQuery = useQuery({
    queryKey: ["admin-agents"],
    queryFn: agentApi.admin.list,
  });

  const { register, handleSubmit, formState: { errors } } = useForm<Form>({
    defaultValues: {
      product_code: product?.product_code ?? "",
      name: product?.name ?? "",
      brand: product?.brand ?? "",
      category_id: product?.category.id ? String(product.category.id) : "",
      vendor_id: product?.vendor_id ? String(product.vendor_id) : "",
      short_description: product?.short_description ?? "",
      description: product?.description ?? "",
      base_price: product?.base_price ?? "0.00",
      compare_at_price: product?.compare_at_price ?? "",
      qualification_point_value: product?.qualification_point_value ?? 0,
      status: product?.status ?? "active",
      tags: Array.isArray(product?.tags) ? product.tags.join(", ") : (product?.tags || ""),
    },
  });

  const save = useMutation({
    mutationFn: (d: Form) => {
      const formData = new FormData();
      formData.append("product_code", d.product_code);
      formData.append("name", d.name);
      formData.append("brand", d.brand);
      formData.append("category_id", String(d.category_id));
      if (d.vendor_id) formData.append("vendor_id", String(d.vendor_id));
      formData.append("short_description", d.short_description);
      formData.append("description", d.description);
      formData.append("base_price", d.base_price);
      if (d.compare_at_price) formData.append("compare_at_price", d.compare_at_price);
      formData.append("qualification_point_value", String(d.qualification_point_value));
      formData.append("status", d.status);
      formData.append("tags", JSON.stringify(d.tags ? d.tags.split(",").map(t => t.trim()) : []));
      
      // Append Image Files
      const fileInput = document.getElementById("product-images") as HTMLInputElement;
      if (fileInput?.files?.length) {
        Array.from(fileInput.files).forEach(file => formData.append("images", file));
        formData.append("thumbnail_index", String(thumbnailIndex));
      }

      // API calls using FormData need different headers!
      const config = {
        headers: { "Content-Type": "multipart/form-data" }
      };

      return isEdit
        ? patch<Product>(`/admin/products/${product!.id}`, formData, config)
        : post<Product>("/admin/products", formData, config);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Product" : "New Product"} size="lg">
      <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
        
        {/* Core Info */}
        <div className="grid grid-cols-2 gap-4">
          <Input label="Product code" placeholder="PRD-001" error={errors.product_code?.message} {...register("product_code", { required: "Required" })} />
          <Input label="Name" placeholder="Product name" error={errors.name?.message} {...register("name", { required: "Required" })} />
        </div>
        
        {/* 📸 Advanced Multi-Image Uploader */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-surface-800 mb-1.5">Product Images</label>
          <div className="p-4 bg-surface-50 border border-surface-200 border-dashed rounded-xl">
            <input 
              type="file" 
              id="product-images" 
              accept="image/*" 
              multiple 
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const urls = files.map(f => URL.createObjectURL(f));
                setPreviewImages(urls);
                setThumbnailIndex(0);
              }}
              className="w-full text-sm text-surface-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
            />
            {previewImages.length > 0 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                {previewImages.map((src, idx) => (
                  <div key={idx} className="relative group shrink-0">
                    <img src={src} className={clsx("w-20 h-20 object-cover rounded-lg border-2", thumbnailIndex === idx ? "border-brand-500" : "border-transparent")} />
                    {thumbnailIndex !== idx && (
                      <button type="button" onClick={() => setThumbnailIndex(idx)} className="absolute inset-0 bg-black/50 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                        Set Primary
                      </button>
                    )}
                    {thumbnailIndex === idx && (
                      <div className="absolute -top-2 -right-2 bg-brand-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">Primary</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <Input label="Brand (Optional)" placeholder="Nike, Samsung..." {...register("brand")} />
          <Select label="Category" placeholder="Select category" options={categories.map((c) => ({ value: String(c.id), label: c.name }))} error={errors.category_id?.message} {...register("category_id", { required: "Required" })} />
        </div>

        {/* Vendor Assign */}
        <div className="bg-brand-50 p-3 rounded-lg border border-brand-200">
          <Select label="Assign to Vendor / Agent (Optional)" placeholder="Platform Default (No Vendor)" 
            options={(agentsQuery.data ?? []).map((a) => ({ value: String(a.id), label: `${a.shop_name || a.full_name} (${a.user_id})` }))}
            {...register("vendor_id")} 
            hint="If assigned, orders for this product will go to the Agent's shop dashboard." />
        </div>

        {/* Description */}
        <Input label="Short Description" placeholder="Highlights for the card view..." {...register("short_description")} />
        <div>
          <label className="block text-sm font-medium text-surface-800 mb-1.5">Full Description</label>
          <textarea {...register("description")} className="w-full h-24 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none" placeholder="Detailed product description…" />
        </div>

        {/* Pricing & Points */}
        <div className="grid grid-cols-3 gap-4">
          <Input label="Base price" type="number" step="0.01" error={errors.base_price?.message} {...register("base_price", { required: "Required" })} />
          <Input label="Compare-at price (Discount)" type="number" step="0.01" hint="Shows a strikethrough price" {...register("compare_at_price")} />
          <Input label="Qualification points" type="number" hint="Can be 0 for Vendor products" {...register("qualification_point_value", { valueAsNumber: true })} />
        </div>

        {/* Status & Tags */}
        <div className="grid grid-cols-2 gap-4">
          <Input label="Tags (Comma separated)" placeholder="new, popular, sale" {...register("tags")} />
          <Select label="Status" options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} {...register("status")} />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-surface-200">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={save.isPending}>
            {isEdit ? "Update product" : "Create product"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}